import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Form,
  Input,
  Modal,
  Select,
  Space,
  Tag,
  Typography,
} from "antd";
import {
  getBankAccountDetails,
  saveBankAccountDetails,
  updateBankAccountDetails,
} from "../../services/bankAccountApi";
import {
  GENERIC_BANK_LOGO,
  ISRAELI_BANKS,
  getIsraeliBankLogoByCode,
  getIsraeliBankNameByCode,
  normalizeIsraeliBankCode,
} from "../../constants/israeliBanks";
import "./BankAccountDetailsCard.scss";

const { Title, Text, Paragraph } = Typography;

const ACCOUNT_TYPE_OPTIONS = [
  { value: "private", label: "פרטי" },
  { value: "business", label: "עסקי" },
];

function accountTypeLabel(v) {
  if (v === "business") return "עסקי";
  if (v === "private") return "פרטי";
  return v || "—";
}

function ReadonlyRow({ label, value }) {
  return (
    <div className="bank-account-details-card__readonly-row">
      <span className="bank-account-details-card__label">{label}</span>
      <span className="bank-account-details-card__value">{value ?? "—"}</span>
    </div>
  );
}

export default function BankAccountDetailsCard() {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [mode, setMode] = useState("view");

  const hasAccount = Boolean(overview?.has_bank_account);
  const bank = overview?.bank_account;

  const bankSelectOptions = useMemo(() => {
    const base = ISRAELI_BANKS.map((b) => ({
      value: b.code,
      label: `${b.name} (${b.code})`,
      logo: b.logo,
    }));
    if (hasAccount && bank?.bank_code) {
      const n = normalizeIsraeliBankCode(bank.bank_code);
      if (n && !ISRAELI_BANKS.some((b) => b.code === n)) {
        return [
          {
            value: n,
            label: `${bank.bank_name || "בנק (לא ברשימה)"} (${n})`,
            logo: GENERIC_BANK_LOGO,
          },
          ...base,
        ];
      }
    }
    return base;
  }, [hasAccount, bank?.bank_code, bank?.bank_name]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getBankAccountDetails();
      setOverview(data);
    } catch (e) {
      setOverview(null);
      setError(e?.response?.data?.message || e?.message || "טעינה נכשלה");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreateOrEdit = () => {
    setMode(hasAccount ? "edit" : "create");
    form.resetFields();
    if (hasAccount && bank) {
      const codeNorm = normalizeIsraeliBankCode(bank.bank_code);
      form.setFieldsValue({
        account_holder_name: bank.account_holder_name,
        bank_code: codeNorm || undefined,
        bank_name:
          getIsraeliBankNameByCode(codeNorm) || String(bank.bank_name || "").trim() || "",
        branch_code: bank.branch_code,
        account_type: bank.account_type || undefined,
        consent_accepted: false,
      });
    } else {
      form.setFieldsValue({ consent_accepted: false });
    }
  };

  const cancelForm = () => {
    form.resetFields();
    setMode("view");
  };

  const buildPayload = (values) => {
    const codeNorm = normalizeIsraeliBankCode(values.bank_code);
    const nameFromList = getIsraeliBankNameByCode(codeNorm);
    return {
      account_holder_name: String(values.account_holder_name || "").trim(),
      account_holder_id: String(values.account_holder_id || "").trim(),
      bank_name: nameFromList || String(values.bank_name || "").trim() || null,
      bank_code: codeNorm || String(values.bank_code || "").replace(/\D/g, ""),
      branch_code: String(values.branch_code || "").trim(),
      account_number: String(values.account_number || "").trim(),
      account_type: values.account_type || null,
      consent_accepted: Boolean(values.consent_accepted),
    };
  };

  const submit = async () => {
    try {
      const values = await form.validateFields();
      const payload = buildPayload(values);
      setSaving(true);
      setError(null);
      if (hasAccount) {
        await updateBankAccountDetails(payload);
        Modal.success({
          title: "עודכן",
          content: "פרטי החשבון עודכנו בהצלחה",
        });
      } else {
        await saveBankAccountDetails(payload);
        Modal.success({
          title: "נשמר",
          content: "פרטי החשבון נשמרו בהצלחה",
        });
      }
      form.resetFields();
      setMode("view");
      await load();
    } catch (e) {
      if (e?.errorFields) throw e;
      const msg = e?.response?.data?.message || e?.message || "שמירה נכשלה";
      setError(msg);
      Modal.error({ title: "שגיאה", content: msg });
      throw e;
    } finally {
      setSaving(false);
    }
  };

  const handleFinish = () => {
    if (!hasAccount) {
      return submit();
    }
    return new Promise((resolve, reject) => {
      Modal.confirm({
        title: "אישור עדכון פרטי חשבון",
        content:
          "האם לעדכן את פרטי החשבון? יש להזין מחדש את כל הפרטים הרגישים ולאשר מחדש את ההצהרה (נהנה יחיד, חיובים וזיכויים לרבות עמלות בהתאם להסכם).",
        okText: "עדכן פרטים",
        cancelText: "בטל",
        onOk: async () => {
          try {
            await submit();
            resolve();
          } catch (err) {
            reject(err);
          }
        },
        onCancel: () => resolve(),
      });
    });
  };

  const showForm = mode === "create" || mode === "edit";

  return (
    <Card
      className="bank-account-details-card"
      loading={loading && !overview}
      title={
        <Space wrap>
          <Title level={4} style={{ margin: 0 }}>
            פרטי חשבון בנק (חיובים וזיכויים)
          </Title>
          {hasAccount ? (
            <Tag color="success">מעודכן</Tag>
          ) : (
            <Tag>לא הוגדר</Tag>
          )}
        </Space>
      }
    >
      <Paragraph type="secondary" style={{ marginBottom: 16 }}>
        כאן ניתן לשמור או לעדכן את פרטי החשבון לצורך חיובים עתידיים, וכן לצורך זיכויים מהחברה (לרבות עמלות) בהתאם להסכם ההתקשרות.
      </Paragraph>

      {error && !showForm ? (
        <Alert
          type="warning"
          showIcon
          message="לא ניתן לטעון את פרטי החשבון"
          description={error}
          action={
            <Button size="small" onClick={load}>
              נסה שוב
            </Button>
          }
          style={{ marginBottom: 16 }}
        />
      ) : null}

      {showForm ? (
        <>
          <Alert
            type="info"
            showIcon
            message="אבטחת מידע"
            description="פרטי חשבון ותעודת זהות אינם נשמרים בדפדפן. הם נשלחים לשרת בלבד ומוצגים חלקית לאחר השמירה."
            style={{ marginBottom: 16 }}
          />
          <Form
            form={form}
            layout="vertical"
            requiredMark="optional"
            disabled={saving}
            onFinish={handleFinish}
          >
            <Form.Item
              label="שם בעל החשבון"
              name="account_holder_name"
              rules={[{ required: true, message: "שדה חובה" }]}
            >
              <Input maxLength={255} autoComplete="off" />
            </Form.Item>
            <Form.Item
              label="תעודת זהות / ח.פ."
              name="account_holder_id"
              rules={[
                { required: true, message: "שדה חובה" },
                {
                  validator: (_, v) => {
                    const d = String(v || "").replace(/\D/g, "");
                    if (d.length < 5 || d.length > 12) {
                      return Promise.reject(new Error("5–12 ספרות לאחר ניקוי"));
                    }
                    return Promise.resolve();
                  },
                },
              ]}
              extra={hasAccount ? "להזנה מלאה מחדש בעת עדכון" : null}
            >
              <Input maxLength={24} autoComplete="off" inputMode="numeric" />
            </Form.Item>
            <Form.Item
              label="בנק"
              name="bank_code"
              rules={[{ required: true, message: "נא לבחור בנק" }]}
            >
              <Select
                showSearch
                allowClear={false}
                placeholder="בחרו בנק מהרשימה"
                optionFilterProp="label"
                options={bankSelectOptions}
                optionRender={(option) => {
                  const data = option.data ?? {};
                  const src = data.logo || GENERIC_BANK_LOGO;
                  return (
                    <span className="bank-account-details-card__bank-option">
                      <img
                        src={src}
                        alt=""
                        className="bank-account-details-card__bank-logo"
                        decoding="async"
                      />
                      <span className="bank-account-details-card__bank-option-text">{data.label}</span>
                    </span>
                  );
                }}
                labelRender={(props) => {
                  const src = getIsraeliBankLogoByCode(props.value);
                  return (
                    <span className="bank-account-details-card__bank-option">
                      <img
                        src={src}
                        alt=""
                        className="bank-account-details-card__bank-logo"
                        decoding="async"
                      />
                      <span className="bank-account-details-card__bank-option-text">{props.label}</span>
                    </span>
                  );
                }}
                popupClassName="bank-account-details-card__bank-select-dropdown"
                listItemHeight={40}
                listHeight={320}
                onChange={(code) => {
                  if (!code) {
                    form.setFieldsValue({ bank_name: "" });
                    return;
                  }
                  const listed = getIsraeliBankNameByCode(String(code));
                  if (listed) {
                    form.setFieldsValue({ bank_name: listed });
                    return;
                  }
                  if (
                    hasAccount &&
                    bank &&
                    normalizeIsraeliBankCode(bank.bank_code) === String(code)
                  ) {
                    form.setFieldsValue({ bank_name: String(bank.bank_name || "").trim() });
                  } else {
                    form.setFieldsValue({ bank_name: "" });
                  }
                }}
              />
            </Form.Item>
            <Form.Item name="bank_name" hidden>
              <Input />
            </Form.Item>
            <Form.Item
              label="מספר סניף"
              name="branch_code"
              rules={[
                { required: true, message: "שדה חובה" },
                { pattern: /^\d{1,3}$/, message: "עד 3 ספרות" },
              ]}
            >
              <Input maxLength={3} autoComplete="off" inputMode="numeric" />
            </Form.Item>
            <Form.Item
              label="מספר חשבון"
              name="account_number"
              rules={[
                { required: true, message: "שדה חובה" },
                { pattern: /^\d{4,15}$/, message: "4–15 ספרות" },
              ]}
            >
              <Input maxLength={15} autoComplete="off" inputMode="numeric" />
            </Form.Item>
            <Form.Item label="סוג חשבון" name="account_type">
              <Select allowClear placeholder="בחרו" options={ACCOUNT_TYPE_OPTIONS} />
            </Form.Item>
            <Form.Item
              name="consent_accepted"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, v) =>
                    v
                      ? Promise.resolve()
                      : Promise.reject(new Error("נדרש אישור")),
                },
              ]}
            >
              <Checkbox>
                <span className="bank-account-details-card__consent-text">
                  אני מצהיר/ה כי אני הנהנה היחיד בחשבון הבנק שפרטיו מוזנים לעיל. אני מאשר/ת שימוש בפרטי החשבון לצורך חיובים עתידיים וכן לצורך זיכויים מצד החברה, לרבות תשלום עמלות המגיעות לי, הכול בהתאם לתנאי המערכת ולהסכם ההתקשרות החל על חשבוני.
                </span>
              </Checkbox>
            </Form.Item>
            <Space wrap>
              <Button type="primary" htmlType="submit" loading={saving}>
                {hasAccount ? "עדכן פרטים" : "שמור פרטים"}
              </Button>
              <Button onClick={cancelForm} disabled={saving}>
                בטל
              </Button>
            </Space>
          </Form>
        </>
      ) : (
        <>
          {!hasAccount ? (
            <Text type="secondary">טרם הוגדרו פרטי חשבון בנק.</Text>
          ) : (
            <div>
              <ReadonlyRow label="שם בעל החשבון" value={bank?.account_holder_name} />
              <ReadonlyRow
                label="בנק"
                value={
                  bank?.bank_name ? (
                    <span className="bank-account-details-card__bank-option bank-account-details-card__bank-option--readonly">
                      <img
                        src={getIsraeliBankLogoByCode(bank.bank_code)}
                        alt=""
                        className="bank-account-details-card__bank-logo"
                        decoding="async"
                      />
                      <span>{bank.bank_name}</span>
                    </span>
                  ) : (
                    "—"
                  )
                }
              />
              <ReadonlyRow label="סניף" value={bank?.branch_code} />
              <ReadonlyRow label="מספר חשבון" value={bank?.account_number_masked} />
              <ReadonlyRow label="תעודת זהות / ח.פ." value={bank?.account_holder_id_masked} />
              <ReadonlyRow label="סוג חשבון" value={accountTypeLabel(bank?.account_type)} />
              <ReadonlyRow label="עדכון אחרון" value={bank?.updated_at} />
            </div>
          )}
          <div style={{ marginTop: 16 }}>
            <Button type="primary" onClick={openCreateOrEdit}>
              {hasAccount ? "ערוך פרטי חשבון" : "הגדרת פרטי חשבון"}
            </Button>
          </div>
        </>
      )}
    </Card>
  );
}
